/**
 * Error-message helpers for FastAPI responses.
 *
 * Pure module (no Sentry/notifications/auth imports) so public/pre-auth pages
 * can use it without pulling in the authenticated apiCall dependency graph.
 */

/**
 * Normalize a FastAPI error `detail` into a human-readable string.
 * - A 401/4xx carries a string detail → returned as-is.
 * - A 422 carries an array of validation objects → join their `.msg` fields.
 * - Anything else (object, null) → `fallback`.
 *
 * Without this, `new Error(<array>)` stringifies to "[object Object]" — the
 * exact bug this fixes. Mirrors the long-standing inline behavior in
 * `apiCall` (api-call.ts), now the single source of truth for both.
 */
export function extractErrorMessage(detail: unknown, fallback: string): string {
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const msg = detail
      .map((d) => (d && typeof d === 'object' ? (d as { msg?: string }).msg ?? '' : String(d)))
      .filter(Boolean)
      .join('; ');
    if (msg) return msg;
  }
  return fallback;
}
